import React, { useState, useEffect, useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Snackbar,
  Alert,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Typography,
  Box,
  DialogContentText,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { ThemeContext } from "../../../../../../../../context/ThemeContextProvider";
import { updateOrder } from "../../../../../../../../services/pos/Tables";

const EditOrderFormTableNumber = ({ open, onClose, initialData, tables }) => {
  const { mode } = useContext(ThemeContext);
  const muiTheme = useTheme();

  // --- responsive hooks ---
  const isSmallTablet = useMediaQuery("(min-width:600px) and (max-width:768px)");
  const isMediumTablet = useMediaQuery("(min-width:769px) and (max-width:900px)");
  const isPortrait = useMediaQuery("(orientation: portrait)");

  const dialogFullScreen = isSmallTablet && isPortrait; // full screen on small tablet portrait
  const inputFontSize = isSmallTablet ? 13 : isMediumTablet ? 14 : 15;
  const btnHeight = isSmallTablet ? 40 : 44;
  const contentPaddingX = isSmallTablet ? 2 : 3;
  // --- end responsive hooks ---

  const [form, setForm] = useState({});
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedTable, setSelectedTable] = useState("");

  useEffect(() => {
    if (initialData) {
      setForm({
        ...initialData,
        customer_id: initialData.customer?.id ?? null,
      });
      setSelectedTable(initialData.table_id || "");
    }
  }, [initialData]);

  const handleTableChange = (e) => {
    setSelectedTable(e.target.value);
  };

  const handleConfirm = async () => {
    try {
      if (!form.order_id) throw new Error("❌ رقم الأوردر غير موجود");

      await updateOrder(form.order_id, {
        table_id: selectedTable,
        customer_id: form.customer_id,
      });

      setSuccess(true);
      setConfirmOpen(false);

      onClose({
        table_id: selectedTable,
        customer_id: form.customer_id,
      });
    } catch (err) {
      setError(err.message || "❌ فشل في نقل الطاولة");
      setConfirmOpen(false);
    }
  };

  const currentTableObj = tables.find((t) => t.id === form.table_id);
  const newTableObj = tables.find((t) => t.id === selectedTable);

  return (
    <>
      <Dialog
        open={open}
        onClose={() => onClose(false)}
        fullWidth
        fullScreen={dialogFullScreen} // responsive behavior
        PaperProps={{
          sx: {
            bgcolor: mode === "dark" ? "#232334" : "#fff",
            color: mode === "dark" ? "#fff" : "#232334",
            borderRadius: dialogFullScreen ? 0 : 3,
            height: dialogFullScreen ? "100dvh" : "auto",
            boxSizing: "border-box",
          },
        }}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setConfirmOpen(true);
          }}
        >
          <DialogTitle
            sx={{
              fontWeight: 700,
              fontSize: isSmallTablet ? 18 : 22,
              textAlign: "center",
              color: "#EA7C69",
              bgcolor: mode === "dark" ? "#181824" : "#fff",
              borderRadius: dialogFullScreen ? 0 : "16px 16px 0 0",
              py: 1.25,
            }}
          >
            نقل الطاولة
          </DialogTitle>
          <DialogContent
            dividers
            sx={{
              bgcolor: mode === "dark" ? "#232334" : "#fff",
              pb: 2,
              px: contentPaddingX,
              pt: 2,
            }}
          >
            <Box mb={2}>
              <Typography sx={{ fontWeight: 600, mb: 1, fontSize: inputFontSize }}>
                الطاولة الحالية:{" "}
                <span style={{ color: "#EA7C69" }}>
                  {currentTableObj?.name || form.table_id || "-"}
                </span>
              </Typography>
            </Box>
            <FormControl
              fullWidth
              sx={{
                bgcolor: mode === "dark" ? "#181824" : "#fff",
                borderRadius: 2,
              }}
            >
              <InputLabel sx={{ color: mode === "dark" ? "#aaa" : undefined }}>
                اختر الطاولة الجديدة
              </InputLabel>
              <Select
                label="اختر الطاولة الجديدة"
                name="table_id"
                value={selectedTable}
                onChange={handleTableChange}
                sx={{
                  color: mode === "dark" ? "#fff" : "#232334",
                  fontSize: inputFontSize,
                  ".MuiOutlinedInput-notchedOutline": {
                    borderColor: mode === "dark" ? "#444" : "#ccc",
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      bgcolor: mode === "dark" ? "#232334" : "#fff",
                      color: mode === "dark" ? "#fff" : "#232334",
                      maxHeight: 360,
                      boxSizing: "border-box",
                    },
                  },
                }}
              >
                {tables && tables.length > 0 ? (
                  tables.map((table) => (
                    <MenuItem key={table.id} value={table.id} sx={{ fontSize: inputFontSize }}>
                      {table.name}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled sx={{ fontSize: inputFontSize }}>
                    لا توجد طاولات متاحة
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions
            sx={{
              bgcolor: mode === "dark" ? "#232334" : "#fff",
              borderRadius: dialogFullScreen ? 0 : "0 0 16px 16px",
              px: 2,
              py: 1.5,
            }}
          >
            <Button
              onClick={() => onClose(false)}
              variant="outlined"
              color="secondary"
              sx={{
                minWidth: 100,
                borderColor: mode === "dark" ? "#888" : undefined,
                color: mode === "dark" ? "#fff" : undefined,
                height: btnHeight,
              }}
            >
              إلغاء
            </Button>
            <Button
              variant="contained"
              type="submit"
              disabled={!selectedTable || selectedTable === form.table_id}
              sx={{
                bgcolor: "#EA7C69",
                color: "#fff",
                minWidth: 120,
                fontWeight: 700,
                height: btnHeight,
                "&:hover": { bgcolor: "#d45d4c" },
              }}
            >
              نقل الطاولة
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Dialog للتأكيد */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth={isSmallTablet ? "xs" : "sm"}
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: mode === "dark" ? "#232334" : "#fff",
            color: mode === "dark" ? "#fff" : "#232334",
            borderRadius: 3,
            boxSizing: "border-box",
          },
        }}
      >
        <DialogTitle
          sx={{ fontWeight: 700, color: "#EA7C69", textAlign: "center", fontSize: isSmallTablet ? 16 : 18 }}
        >
          تأكيد نقل الطاولة
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{
              color: mode === "dark" ? "#fff" : "#232334",
              fontWeight: 500,
              fontSize: inputFontSize,
            }}
          >
            هل أنت متأكد من نقل الطلب من الطاولة
            <span
              style={{ color: "#EA7C69", fontWeight: 700, margin: "0 6px" }}
            >
              {currentTableObj?.name || form.table_id || "-"}
            </span>
            إلى الطاولة
            <span
              style={{ color: "#EA7C69", fontWeight: 700, margin: "0 6px" }}
            >
              {newTableObj?.name || selectedTable || "-"}
            </span>
            ؟
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 2 }}>
          <Button
            onClick={() => setConfirmOpen(false)}
            color="secondary"
            variant="outlined"
            sx={{ height: btnHeight }}
          >
            إلغاء
          </Button>
          <Button
            onClick={handleConfirm}
            color="primary"
            variant="contained"
            sx={{ bgcolor: "#EA7C69", height: btnHeight }}
          >
            نعم، نقل الطاولة
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!error}
        autoHideDuration={3000}
        onClose={() => setError("")}
      >
        <Alert severity="error">{error}</Alert>
      </Snackbar>
      <Snackbar
        open={success}
        autoHideDuration={3000}
        onClose={() => setSuccess(false)}
      >
        <Alert severity="success">✅ تم نقل الطاولة بنجاح</Alert>
      </Snackbar>
    </>
  );
};

export default EditOrderFormTableNumber;
